import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Button,
  Checkbox,
  DatePicker,
  Drawer,
  Form,
  Select,
  Space,
  Spin,
  TimePicker,
  message
} from "antd";
import dayjs from "dayjs";
import ClientHistoryEditor from "./client-history-editor";
import {
  buildDiaryEditorSections,
  editorSectionsToDiaryParts,
  getDiaryPartSchema
} from "./client-diary-parts";
import { sectionsSnapshot } from "./client-history-doc";
import {
  createHealthClientDiary,
  fetchHealthClientDiaryEntry,
  fetchHealthDiaryConditions,
  fetchHealthDiaryVisitTypes,
  fetchHealthDoctors,
  updateHealthClientDiary
} from "./health-clients-api";
import { pick, toNumber } from "./health-clients-utils";

const normalizeVisitType = (row) => ({
  id: toNumber(pick(row, "id", "Id")),
  name: pick(row, "name", "Name") ?? "",
  id1: toNumber(pick(row, "id1", "Id1")) ?? 0
});

const normalizeCondition = (row) => ({
  id: toNumber(pick(row, "id", "Id")),
  name: pick(row, "name", "Name") ?? ""
});

const normalizeDoctor = (row) => ({
  idSotr: toNumber(pick(row, "idSotr", "IdSotr", "id", "Id")),
  name: pick(row, "name", "Name", "sotrName", "SotrName") ?? ""
});

const normalizePart = (row) => ({
  idSpr: toNumber(pick(row, "idSpr", "IdSpr")),
  name: pick(row, "name", "Name") ?? "",
  value: pick(row, "fValue", "FValue", "value", "Value") ?? ""
});

const applyEntryToForm = (entry, visitList, form, options = {}) => {
  const { asCopy = false, userId } = options;
  const fdate = pick(entry, "fdate", "Fdate") ? dayjs(pick(entry, "fdate", "Fdate")) : dayjs();
  const dtStart = pick(entry, "dtStart", "DtStart") ? dayjs(pick(entry, "dtStart", "DtStart")) : null;
  const dtFinish = pick(entry, "dtFinish", "DtFinish") ? dayjs(pick(entry, "dtFinish", "DtFinish")) : null;
  const entryIdSpr = toNumber(pick(entry, "idSpr", "IdSpr"));
  const entryId1 =
    toNumber(pick(entry, "vidId1", "VidId1")) ??
    visitList.find((v) => v.id === entryIdSpr)?.id1 ??
    0;

  form.setFieldsValue({
    fdate: asCopy ? dayjs() : fdate,
    dtStart: asCopy ? dayjs().subtract(30, "minute") : dtStart,
    dtFinish: asCopy ? dayjs() : dtFinish,
    idSotr: toNumber(pick(entry, "idSotr", "IdSotr")) || userId || undefined,
    idSpr: entryIdSpr || undefined,
    conditionId: toNumber(pick(entry, "conditionId", "ConditionId")) || 1,
    isFirst: (toNumber(pick(entry, "isFirst", "IsFirst")) ?? 0) === 1
  });

  const parts = (pick(entry, "parts", "Parts") || []).map(normalizePart);
  return buildDiaryEditorSections(entryId1, parts);
};

/**
 * Drawer добавления/изменения записи дневника.
 * copyFromIdStr — открыть как новую запись с данными исходной (копия).
 */
const ClientDiaryForm = ({ idClient, idStr, copyFromIdStr = null, open, onClose, onSaved }) => {
  const isNew = idStr == null;
  const isCopy = isNew && copyFromIdStr != null;
  const userId = useSelector((state) => state.user.session?.userId);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [visitTypes, setVisitTypes] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [editorSections, setEditorSections] = useState([]);
  const [savedSnap, setSavedSnap] = useState("[]");
  const [editorKey, setEditorKey] = useState(0);
  const [readOnly, setReadOnly] = useState(false);
  const hydratingRef = useRef(false);

  const idSprWatch = Form.useWatch("idSpr", form);
  const selectedVid = useMemo(
    () => visitTypes.find((v) => v.id === idSprWatch),
    [visitTypes, idSprWatch]
  );
  const id1 = selectedVid?.id1 ?? 0;

  const dirty = sectionsSnapshot(editorSections) !== savedSnap;

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    setLoading(true);

    const sourceIdStr = isCopy ? copyFromIdStr : isNew ? null : idStr;

    Promise.all([
      fetchHealthDiaryVisitTypes(),
      fetchHealthDiaryConditions(),
      fetchHealthDoctors(),
      sourceIdStr != null ? fetchHealthClientDiaryEntry(idClient, sourceIdStr) : Promise.resolve(null)
    ])
      .then(([types, conds, docs, entry]) => {
        if (cancelled) return;
        const visitList = (Array.isArray(types) ? types : []).map(normalizeVisitType).filter((v) => v.id);
        const condList = (Array.isArray(conds) ? conds : []).map(normalizeCondition).filter((c) => c.id);
        const docList = (Array.isArray(docs) ? docs : []).map(normalizeDoctor).filter((d) => d.idSotr);
        setVisitTypes(visitList);
        setConditions(condList);
        setDoctors(docList);

        if (entry && (isCopy || !isNew)) {
          if (!isCopy) {
            const approved =
              Boolean(pick(entry, "isApproved", "IsApproved")) ||
              (toNumber(pick(entry, "idSotrUtverdil", "IdSotrUtverdil")) ?? 0) > 0;
            setReadOnly(approved);
          } else {
            setReadOnly(false);
          }
          const sections = applyEntryToForm(entry, visitList, form, { asCopy: isCopy, userId });
          hydratingRef.current = true;
          setEditorSections(sections);
          // Копия сразу «грязная» — нужно сохранить как новую запись.
          setSavedSnap(isCopy ? "[]" : sectionsSnapshot(sections));
          setEditorKey((k) => k + 1);
          return;
        }

        const defaultVid = visitList[0]?.id;
        const defaultId1 = visitList[0]?.id1 ?? 0;
        const now = dayjs();
        form.setFieldsValue({
          fdate: now,
          dtStart: now.subtract(30, "minute"),
          dtFinish: now,
          idSotr: userId || undefined,
          idSpr: defaultVid,
          conditionId: condList[0]?.id ?? 1,
          isFirst: true
        });
        const sections = buildDiaryEditorSections(defaultId1, []);
        hydratingRef.current = true;
        setEditorSections(sections);
        setSavedSnap(sectionsSnapshot(sections));
        setReadOnly(false);
        setEditorKey((k) => k + 1);
      })
      .catch((e) => message.error(e.message || "Не удалось загрузить запись"))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, idClient, idStr, copyFromIdStr, isNew, isCopy, form, userId]);

  // Смена вида → пересобрать схему разделов, сохранив общие тексты по idSpr.
  useEffect(() => {
    if (!open || loading) return;
    const schema = getDiaryPartSchema(id1);
    setEditorSections((prev) => {
      const byId = new Map((prev || []).map((s) => [s.idPart, s.value ?? ""]));
      const next = schema.map((s) => ({
        idPart: s.idSpr,
        name: s.name,
        value: byId.has(s.idSpr) ? byId.get(s.idSpr) : ""
      }));
      if (sectionsSnapshot(next) === sectionsSnapshot(prev)) return prev;
      setEditorKey((k) => k + 1);
      return next;
    });
  }, [id1, open, loading]);

  const handleEditorChange = (sections) => {
    const mapped = (sections || []).map((s) => {
      const meta = editorSections.find((x) => x.idPart === s.idPart);
      return { idPart: s.idPart, name: meta?.name ?? "", value: s.value ?? "" };
    });
    if (hydratingRef.current) {
      hydratingRef.current = false;
      setEditorSections(mapped);
      if (!isCopy) setSavedSnap(sectionsSnapshot(mapped));
      return;
    }
    setEditorSections(mapped);
  };

  const handleSave = async () => {
    if (readOnly || saving) return;
    try {
      const values = await form.validateFields();
      const body = {
        fdate: values.fdate ? values.fdate.format("YYYY-MM-DD") : null,
        dtStart: values.dtStart ? values.dtStart.toISOString() : null,
        dtFinish: values.dtFinish ? values.dtFinish.toISOString() : null,
        idSotr: values.idSotr,
        idSpr: values.idSpr,
        conditionId: values.conditionId,
        isFirst: values.isFirst ? 1 : 0,
        parts: editorSectionsToDiaryParts(editorSections)
      };

      setSaving(true);
      if (isNew) {
        await createHealthClientDiary(idClient, body);
        message.success("Запись добавлена");
      } else {
        await updateHealthClientDiary(idClient, idStr, body);
        message.success("Запись сохранена");
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.message || "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      title={isCopy ? "Копия записи в дневник" : isNew ? "Новая запись в дневник" : "Запись в дневник"}
      open={open}
      onClose={onClose}
      width={Math.min(920, typeof window !== "undefined" ? window.innerWidth - 24 : 920)}
      destroyOnHidden
      className="client-diary-form-drawer"
      styles={{ body: { display: "flex", flexDirection: "column" } }}
      extra={
        <Space>
          {!readOnly && (
            <Button type="primary" loading={saving} onClick={handleSave}>
              Сохранить
            </Button>
          )}
          <Button onClick={onClose}>Закрыть</Button>
        </Space>
      }
    >
      {loading ? (
        <div className="client__center">
          <Spin tip="Загрузка..." />
        </div>
      ) : (
        <div className="client-diary-form">
          {readOnly && (
            <div className="client-diary-form__readonly">Запись утверждена — только просмотр</div>
          )}
          <Form form={form} layout="vertical" disabled={readOnly} className="client-diary-form__meta">
            <div className="client-diary-form__row">
              <Form.Item name="fdate" label="Дата" rules={[{ required: true, message: "Укажите дату" }]}>
                <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item name="dtStart" label="Начало">
                <TimePicker format="HH:mm" style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item name="dtFinish" label="Окончание">
                <TimePicker format="HH:mm" style={{ width: "100%" }} />
              </Form.Item>
            </div>
            <div className="client-diary-form__row">
              <Form.Item name="idSotr" label="Врач" rules={[{ required: true, message: "Укажите врача" }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={doctors.map((d) => ({ value: d.idSotr, label: d.name }))}
                />
              </Form.Item>
              <Form.Item name="idSpr" label="Вид" rules={[{ required: true, message: "Укажите вид" }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={visitTypes.map((v) => ({ value: v.id, label: v.name }))}
                />
              </Form.Item>
              <Form.Item name="conditionId" label="Состояние">
                <Select options={conditions.map((c) => ({ value: c.id, label: c.name }))} />
              </Form.Item>
            </div>
            <Form.Item name="isFirst" valuePropName="checked">
              <Checkbox>Первичный визит</Checkbox>
            </Form.Item>
          </Form>

          <div className="client-diary-form__editor">
            <ClientHistoryEditor
              key={editorKey}
              sections={editorSections}
              onChange={handleEditorChange}
              editable={!readOnly}
            />
          </div>
          {dirty && !readOnly && <div className="client-diary-form__dirty">Есть несохранённые изменения текста</div>}
        </div>
      )}
    </Drawer>
  );
};

export default ClientDiaryForm;
